import { useState, useEffect, useCallback, useMemo } from "react";
import { useQuery, useSubscription } from "@apollo/client";
import {
  GET_PORTFOLIO_PNL,
  GET_PERFORMANCE_METRICS,
  PNL_UPDATES_SUBSCRIPTION,
} from "../graphql/pnl";
import { PnLCalculationService } from "../services/pnlCalculationService";
import {
  PnLCalculation,
  PositionPnL,
  TradePnL,
  PerformanceMetrics,
  PnLUpdate,
} from "../types/pnl";
import { Position, Trade } from "../types/position";

interface UsePnLReturn {
  // Portfolio-level data
  portfolioPnL: PnLCalculation | null;
  performanceMetrics: PerformanceMetrics | null;

  // Trade-level data
  tradePnLs: TradePnL[];
  positionPnLs: PositionPnL[];

  // Loading and error states
  loading: boolean;
  error: any;

  // Real-time updates
  pnlUpdates: PnLUpdate[];

  // Calculation methods
  recalculatePnL: () => void;
  getTradePnL: (tradeId: number) => TradePnL | null;
  getPositionPnL: (positionId: string) => PositionPnL | null;

  // Market price management
  currentPrices: Record<string, number>;
  updateCurrentPrice: (symbol: string, price: number) => void;

  // Date filtering
  dateRange: { start: string; end: string } | null;
  setDateRange: (range: { start: string; end: string } | null) => void;
}

interface UsePnLOptions {
  includeFees?: boolean;
  autoRefresh?: boolean;
  refreshInterval?: number;
  initialDateRange?: { start: string; end: string };
}

export const usePnL = (options: UsePnLOptions = {}): UsePnLReturn => {
  const {
    includeFees = true,
    autoRefresh = true,
    refreshInterval = 30000, // 30 seconds
    initialDateRange = null,
  } = options;

  // State
  const [currentPrices, setCurrentPrices] = useState<Record<string, number>>(
    {},
  );
  const [dateRange, setDateRange] = useState<{
    start: string;
    end: string;
  } | null>(initialDateRange);
  const [pnlUpdates, setPnlUpdates] = useState<PnLUpdate[]>([]);
  const [lastCalculation, setLastCalculation] = useState<Date>(new Date());

  // GraphQL queries
  const { data, loading, error, refetch } = useQuery(GET_PORTFOLIO_PNL, {
    variables: { includeFees },
    fetchPolicy: "cache-and-network",
    pollInterval: autoRefresh ? refreshInterval : 0,
  });

  // Real-time subscription for P&L updates
  const { data: subscriptionData } = useSubscription(PNL_UPDATES_SUBSCRIPTION);

  // Process subscription updates
  useEffect(() => {
    if (subscriptionData?.pnlUpdate) {
      const update = subscriptionData.pnlUpdate;
      setPnlUpdates((prev) => [update, ...prev.slice(0, 99)]); // Keep last 100 updates
      setLastCalculation(new Date());
    }
  }, [subscriptionData]);

  // Initialize current prices from position data
  useEffect(() => {
    if (data?.trades) {
      const prices: Record<string, number> = {};
      data.trades.forEach((trade: Trade) => {
        if (trade.positions && trade.positions.length > 0) {
          // Use the latest position price as current price
          const latestPosition = trade.positions[trade.positions.length - 1];
          prices[trade.symbol] = latestPosition.price;
        }
      });
      setCurrentPrices(prices);
    }
  }, [data]);

  // Calculate portfolio P&L
  const portfolioPnL = useMemo(() => {
    if (!data?.trades) return null;

    return PnLCalculationService.calculatePortfolioPnL(
      data.trades,
      data.trades.flatMap((t: Trade) => t.positions || []),
      currentPrices,
      includeFees,
    );
  }, [data, currentPrices, includeFees]);

  // Calculate performance metrics
  const performanceMetrics = useMemo(() => {
    if (!data?.trades) return null;

    return PnLCalculationService.calculatePerformanceMetrics(
      data.trades,
      data.trades.flatMap((t: Trade) => t.positions || []),
      currentPrices,
      includeFees,
    );
  }, [data, currentPrices, includeFees]);

  // Calculate trade-level P&L
  const tradePnLs = useMemo(() => {
    if (!data?.trades) return [];

    return data.trades.map((trade: Trade) =>
      PnLCalculationService.calculateTradePnL(
        trade,
        trade.positions || [],
        currentPrices[trade.symbol] || 0,
        includeFees,
      ),
    );
  }, [data, currentPrices, includeFees]);

  // Calculate position-level P&L
  const positionPnLs = useMemo(() => {
    if (!data?.trades) return [];

    const allPositions: Position[] = [];
    data.trades.forEach((trade: Trade) => {
      if (trade.positions) {
        trade.positions.forEach((position) => {
          allPositions.push({
            ...position,
            trade: trade,
          });
        });
      }
    });

    return allPositions.map((position) =>
      PnLCalculationService.calculatePositionPnL(
        position,
        currentPrices[position.trade?.symbol || ""] || position.price,
        includeFees,
      ),
    );
  }, [data, currentPrices, includeFees]);

  // Helper methods
  const recalculatePnL = useCallback(() => {
    refetch();
    setLastCalculation(new Date());
  }, [refetch]);

  const getTradePnL = useCallback(
    (tradeId: number): TradePnL | null => {
      return tradePnLs.find((t) => t.tradeId === tradeId) || null;
    },
    [tradePnLs],
  );

  const getPositionPnL = useCallback(
    (positionId: string): PositionPnL | null => {
      return positionPnLs.find((p) => p.positionId === positionId) || null;
    },
    [positionPnLs],
  );

  const updateCurrentPrice = useCallback((symbol: string, price: number) => {
    setCurrentPrices((prev) => ({
      ...prev,
      [symbol]: price,
    }));
    setLastCalculation(new Date());
  }, []);

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      recalculatePnL();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, recalculatePnL]);

  // Filter data by date range if specified
  const filteredData = useMemo(() => {
    if (!dateRange || !data?.trades) return data;

    const filteredTrades = data.trades.filter((trade: Trade) => {
      const tradeDate = new Date(trade.date);
      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);
      return tradeDate >= startDate && tradeDate <= endDate;
    });

    return { ...data, trades: filteredTrades };
  }, [data, dateRange]);

  return {
    // Portfolio-level data
    portfolioPnL,
    performanceMetrics,

    // Trade-level data
    tradePnLs,
    positionPnLs,

    // Loading and error states
    loading,
    error,

    // Real-time updates
    pnlUpdates,

    // Calculation methods
    recalculatePnL,
    getTradePnL,
    getPositionPnL,

    // Market price management
    currentPrices,
    updateCurrentPrice,

    // Date filtering
    dateRange,
    setDateRange,
  };
};

// Specialized hook for single trade P&L
export const useTradePnL = (tradeId: number, options: UsePnLOptions = {}) => {
  const { includeFees = true } = options;

  const { data, loading, error } = useQuery(GET_PORTFOLIO_PNL, {
    variables: { includeFees },
    fetchPolicy: "cache-and-network",
  });

  const tradePnL = useMemo(() => {
    if (!data?.trades) return null;

    const trade = data.trades.find((t: Trade) => t.id === tradeId);
    if (!trade) return null;

    // For single trade, we need to estimate current price
    const currentPrice =
      trade.positions?.[trade.positions.length - 1]?.price || 0;

    return PnLCalculationService.calculateTradePnL(
      trade,
      trade.positions || [],
      currentPrice,
      includeFees,
    );
  }, [data, tradeId, includeFees]);

  return {
    tradePnL,
    loading,
    error,
  };
};

// Hook for performance metrics with date filtering
export const usePerformanceMetrics = (options: UsePnLOptions = {}) => {
  const { includeFees = true, initialDateRange = null } = options;

  const [dateRange, setDateRange] = useState<{
    start: string;
    end: string;
  } | null>(initialDateRange);

  const { data, loading, error, refetch } = useQuery(GET_PERFORMANCE_METRICS, {
    variables: {
      includeFees,
      startDate: dateRange?.start,
      endDate: dateRange?.end,
    },
    fetchPolicy: "cache-and-network",
    skip: !dateRange,
  });

  const performanceMetrics = useMemo(() => {
    if (!data?.trades) return null;

    // Estimate current prices from latest positions
    const currentPrices: Record<string, number> = {};
    data.trades.forEach((trade: Trade) => {
      if (trade.positions && trade.positions.length > 0) {
        const latestPosition = trade.positions[trade.positions.length - 1];
        currentPrices[trade.symbol] = latestPosition.price;
      }
    });

    return PnLCalculationService.calculatePerformanceMetrics(
      data.trades,
      data.trades.flatMap((t: Trade) => t.positions || []),
      currentPrices,
      includeFees,
    );
  }, [data, includeFees]);

  return {
    performanceMetrics,
    loading,
    error,
    dateRange,
    setDateRange,
    refetch,
  };
};
